import type { ProductionRun } from "./models";

/**
 * Applies a mutation response only after the server has confirmed it. This
 * keeps the operator ledger responsive without treating a button click as a
 * successful state transition.
 */
export function replaceConfirmedRun(runs: ProductionRun[], confirmedRun: ProductionRun, insertMissing = false) {
  if (insertMissing && !runs.some(run => run.id === confirmedRun.id)) return [confirmedRun, ...runs];
  return runs.map((run) => run.id === confirmedRun.id && run.version <= confirmedRun.version ? confirmedRun : run);
}

/** The run ledger owns open/closed downtime. Slow auxiliary payloads cannot delay it. */
export async function resolveRunsBeforeAuxiliary<Runs, Auxiliary>(
  runs: Promise<Runs>,
  auxiliary: Promise<Auxiliary>,
  applyRuns: (value: Runs) => void,
) {
  const resolvedRuns = await runs;
  applyRuns(resolvedRuns);
  return { runs: resolvedRuns, auxiliary: await auxiliary };
}

/** Last request wins across full and counter-triggered run lanes. */
export class AuthoritativeRunsResponseOwnership {
  private sequence = 0;
  private current: { scope: string; id: number } | null = null;
  start(scope: string) {
    const ticket = { scope, id: ++this.sequence };
    this.current = ticket;
    return ticket;
  }
  owns(ticket: { scope: string; id: number }) {
    return this.current?.scope === ticket.scope && this.current.id === ticket.id;
  }
  invalidate() {
    this.current = null;
  }
}

/** Auxiliary responses only compete with auxiliary responses for the same scope.
 * A five-second ledger poll must not discard a still-valid notification response. */
export class AuxiliaryResponseOwnership {
  private sequence = 0;
  private current: { scope: string; id: number } | null = null;
  start(scope: string) {
    const ticket = { scope, id: ++this.sequence };
    this.current = ticket;
    return ticket;
  }
  owns(ticket: { scope: string; id: number }) {
    return this.current?.scope === ticket.scope && this.current.id === ticket.id;
  }
  invalidate() {
    this.current = null;
  }
}

/** A counter-version change must not create overlapping priority run requests. */
export class AuthoritativeRunsRefreshLane {
  private active: { scope: string; id: number } | null = null;
  private nextId = 0;
  start(scope: string) {
    if (this.active?.scope === scope) return null;
    const ticket = { scope, id: ++this.nextId };
    this.active = ticket;
    return ticket;
  }
  owns(ticket: { scope: string; id: number }) {
    return this.active?.scope === ticket.scope && this.active.id === ticket.id;
  }
  finish(ticket: { scope: string; id: number }) {
    if (this.owns(ticket)) this.active = null;
  }
}

/** The fast lane only refreshes an already-ready screen; it cannot replace initial reference loading. */
export function canStartAuthoritativeRunsRefresh(loadedScope: string | null, scope: string) {
  return loadedScope === scope;
}

/** A confirmed mutation belongs only to the still-ready terminal/date scope. */
export function canApplyConfirmedDowntime(readyScope: string | null, currentScope: string, scope: string) {
  return readyScope === scope && currentScope === scope;
}

/** A full refresh may update shared connection state only while it still owns the ledger. */
export function canCommitFullLoadState(input: {
  latestRequest: boolean;
  currentScope: boolean;
  ownsRunLedger: boolean;
}) {
  return input.latestRequest && input.currentScope && input.ownsRunLedger;
}

export function shouldSurfaceProductionLoadFailure(
  runsApplied: boolean,
  requiresReferenceForReadiness: boolean,
  referenceLoaded: boolean,
) {
  return !runsApplied || (requiresReferenceForReadiness && !referenceLoaded);
}
